// 📄 pages/Ariba/sections/TransactionCatalogToolbox.tsx
// Acceso directo (standalone, sin proyectoId) al Catálogo de Transacciones
// SAP desde el Ariba Toolbox — mismo catálogo global de solo lectura que
// /proyecto/:projectId/transacciones (ver [[TransaccionesCatalogo.tsx]]),
// pero accesible sin estar dentro de un proyecto puntual. Mismo patrón que
// [[BadiCatalogToolbox.tsx]].
import { useEffect, useRef, useState } from 'react';
import { RefreshCw, Terminal, Info } from 'lucide-react';
import Breadcrumbs from '../../../components/ui/Breadcrumbs.tsx';
import { TransaccionSap, getTransacciones, seedCatalogoTransacciones } from '../../../services/catalogoTransaccionesService.ts';
import TransactionCatalogGrid from '../../../components/abap/TransactionCatalogGrid.tsx';
import { useAuth } from '../../../context/AuthContext.tsx';

interface TransactionCatalogToolboxProps {
  setPage?: (page: string) => void;
}

export default function TransactionCatalogToolbox({ setPage }: TransactionCatalogToolboxProps) {
  const { profile } = useAuth();
  const esAdmin = profile?.role === 'admin';
  const [transacciones, setTransacciones] = useState<TransaccionSap[]>([]);
  const [loading, setLoading] = useState(true);
  const [sembrando, setSembrando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const seedIntentado = useRef(false);

  useEffect(() => {
    (async () => {
      try {
        let data = await getTransacciones();
        if (data.length === 0 && esAdmin && !seedIntentado.current) {
          seedIntentado.current = true;
          setSembrando(true);
          try {
            await seedCatalogoTransacciones();
            data = await getTransacciones();
          } finally {
            setSembrando(false);
          }
        }
        setTransacciones(data);
      } catch (err) {
        console.error('Error cargando el catálogo de transacciones:', err);
        setError('No se pudo cargar el catálogo de transacciones.');
      } finally {
        setLoading(false);
      }
    })();
  }, [esAdmin]);

  return (
    <main className={setPage ? 'min-h-screen p-4 md:p-8 text-white' : 'text-white'}>
      {setPage && (
        <div className="max-w-6xl mx-auto mb-6">
          <Breadcrumbs currentPage="transacciones-toolbox" setPage={setPage} />
        </div>
      )}

      <div className="max-w-6xl mx-auto space-y-6">
        <header>
          <h1 className="text-2xl font-light text-white">Catálogo de Transacciones SAP</h1>
          <p className="text-sm text-gray-400 mt-1">Guía y diccionario rápido de transacciones de desarrollo, monitoreo y compras en SAP.</p>
        </header>

        <div className="bg-blue-500/10 border border-blue-500/20 text-blue-300 p-3 rounded-lg text-xs flex items-start gap-2">
          <Info className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <span>Catálogo global de referencia técnica, mantenido desde el panel de Administración. Filtra por Categoría o Solución y usa el botón de copiar para llevarte el código de transacción directo al portapapeles.</span>
        </div>

        {error && (
          <div className="bg-red-500/10 border border-red-500/30 text-red-400 p-3 rounded-lg text-xs">{error}</div>
        )}

        {loading ? (
          <div className="p-10 text-center text-gray-400 flex flex-col items-center gap-3">
            <RefreshCw className="w-6 h-6 animate-spin text-blue-500" />
            <p className="text-sm">{sembrando ? 'Inicializando catálogo con datos de ejemplo...' : 'Cargando catálogo...'}</p>
          </div>
        ) : transacciones.length === 0 ? (
          <div className="p-10 text-center text-gray-500 bg-white/5 border border-white/10 rounded-2xl">
            <Terminal className="w-8 h-8 mx-auto mb-2 text-gray-600" />
            <p className="text-sm">Todavía no hay transacciones registradas en el catálogo. Un Admin puede cargarlas desde /admin.</p>
          </div>
        ) : (
          <TransactionCatalogGrid transacciones={transacciones} readOnly />
        )}
      </div>
    </main>
  );
}
